import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, BookOpen, Newspaper, Sigma, Atom } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Admin() {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/lessons", { params: { admin: true } }).then((r) => setLessons(r.data)).catch(() => {});
    api.get("/articles", { params: { admin: true } }).then((r) => setArticles(r.data)).catch(() => {});
  }, []);

  const bySubject = (s) => lessons.filter((l) => l.subject === s);

  return (
    <div>
      <div className="bg-nasa-blue text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-28 pb-12">
          <div className="flex items-center gap-3 mb-3"><span className="accent-rule-white" /><span className="eyebrow text-white/90">PANEL DE ADMINISTRACIÓN</span></div>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Hola, {user?.name || "Admin"}</h1>
          <p className="text-white/85 mt-3">Gestiona los apartados de Aprende y los artículos del Noticiero.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Sigma, n: bySubject("matematicas").length, l: "APARTADOS MATEMÁTICAS" },
            { icon: Atom, n: bySubject("fisica").length, l: "APARTADOS FÍSICA" },
            { icon: Newspaper, n: articles.length, l: "ARTÍCULOS" },
          ].map((c) => (
            <div key={c.l} className="bg-white border border-paper-200 border-t-4 border-t-nasa-blue p-6">
              <c.icon className="text-nasa-blue" size={28} />
              <div className="font-display font-extrabold text-4xl mt-3 text-nasa-blue">{c.n}</div>
              <p className="eyebrow text-ink-500 mt-1">{c.l}</p>
            </div>
          ))}
        </div>

        <section className="mt-14">
          <div className="flex items-center justify-between mb-5 pb-3 border-b-2 border-paper-200">
            <h2 className="font-display font-extrabold text-2xl flex items-center gap-2 text-nasa-blue"><BookOpen size={22} /> Apartados (Aprende)</h2>
            <div className="flex gap-2">
              <Link to="/admin/leccion/nueva?subject=matematicas" data-testid="admin-new-math" className="inline-flex items-center gap-1 text-sm font-semibold border border-paper-300 px-3 py-2 rounded-full hover:border-nasa-blue hover:text-nasa-blue transition-colors"><Plus size={15} /> Matemáticas</Link>
              <Link to="/admin/leccion/nueva?subject=fisica" data-testid="admin-new-physics" className="inline-flex items-center gap-1 text-sm font-semibold border border-paper-300 px-3 py-2 rounded-full hover:border-nasa-blue hover:text-nasa-blue transition-colors"><Plus size={15} /> Física</Link>
            </div>
          </div>
          <div className="border border-paper-200">
            {lessons.length === 0 ? (
              <p className="p-8 text-ink-400 text-center">Sin apartados todavía.</p>
            ) : lessons.map((l) => (
              <div key={l.id} className="flex items-center justify-between px-5 py-4 border-b border-paper-100 last:border-0 hover:bg-paper-50" data-testid={`admin-lesson-row-${l.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`eyebrow text-[10px] px-2 py-1 ${l.subject === "matematicas" ? "bg-nasa-blue/10 text-nasa-blue" : "bg-paper-100 text-ink-700"}`}>{l.subject === "matematicas" ? "MAT" : "FÍS"}</span>
                  <span className="font-medium truncate text-ink">{l.title}</span>
                  {!l.published && <span className="eyebrow text-[9px] bg-paper-100 text-ink-500 px-2 py-0.5">BORRADOR</span>}
                </div>
                <Link to={`/admin/leccion/${l.id}`} className="text-ink-500 hover:text-nasa-blue p-2"><Pencil size={15} /></Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <div className="flex items-center justify-between mb-5 pb-3 border-b-2 border-paper-200">
            <h2 className="font-display font-extrabold text-2xl flex items-center gap-2 text-nasa-blue"><Newspaper size={22} /> Artículos (Noticiero)</h2>
            <Link to="/admin/articulo/nuevo" data-testid="admin-new-article" className="inline-flex items-center gap-1 bg-nasa-blue text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-nasa-navy transition-colors"><Plus size={15} /> Nuevo artículo</Link>
          </div>
          <div className="border border-paper-200">
            {articles.length === 0 ? (
              <p className="p-8 text-ink-400 text-center">Sin artículos todavía.</p>
            ) : articles.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-5 py-4 border-b border-paper-100 last:border-0 hover:bg-paper-50" data-testid={`admin-article-row-${a.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {a.featured && <span className="eyebrow text-[9px] bg-nasa-blue/10 text-nasa-blue px-2 py-0.5">DESTACADO</span>}
                  <span className="font-medium truncate text-ink">{a.title}</span>
                  {!a.published && <span className="eyebrow text-[9px] bg-paper-100 text-ink-500 px-2 py-0.5">BORRADOR</span>}
                </div>
                <Link to={`/admin/articulo/${a.id}`} className="text-ink-500 hover:text-nasa-blue p-2"><Pencil size={15} /></Link>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
